import * as React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { DetectIsMobile } from 'utils/mobileDetect';
import { ActionRenderer } from './PageActions/ActionRender';
import { useLayoutSlice } from '../../slice';
import {
  selectExpandedSidePanel,
  selectHideNavigation,
  selectIsCoverExpanded,
  selectIsCoverOpen,
} from '../../slice/selectors';
import clsx from 'clsx';
import { CoverWrapper } from '../CoverWrapper';
import { Head } from './components/Head';
import { ShortHead } from './components/ShortHead';
import { ButtonsPanel } from './components/ButtonsPanel';
import { TopButtonsPanel } from './components/TopButtonsPanel';
import { SwitchActionsProps } from './PageActions/SwitchActions';
import { ButtonsSkeleton, FormSkeleton } from './components/FormSkeleton';
import styled from 'styled-components';
import { useTheme } from '@material-ui/styles';
import { bookitShadows } from 'styles/shadows/bookitShadow';
import { bookitColors } from 'styles/colors/bookitColors';
import {
  selectIsSubBarCollapsed,
  selectShowGlobalServiceGroupFilter,
} from 'app/slice/selectors';
export type PageContentRenderer = () => React.ReactNode;

export interface PageWrapperProps extends CoverProps {
  children: React.ReactNode;
  pageName?: React.ReactNode;
  titlePage?: string;
  subTitlePage?: React.ReactNode;
  leftActions?: ActionRenderer[];
  rightActions?: ActionRenderer[];
  rightTopActions?: ActionRenderer[];
  leftTopActions?: ActionRenderer[];
  onSubmit?: () => void;
  onClose?: () => void;
  closeSidePanel?: () => void;
  closable?: boolean;
  useSidePanel: boolean;
  loading?: boolean;
  confirm?: JSX.Element;
  pageLink?: string;
  toPageLink?: string;
  cover?: React.ReactNode;
  disableExpandToggle?: boolean;
  titleTooltip?: string;
  switchState?: SwitchActionsProps;
  useSwitchActions?: boolean;
  topProcessing?: boolean;
  maxLeftTopButtonsLength?: number;
  rightTopActionsAsMenu?: boolean;
  hideTitleBar?: boolean;
  leftActionsMaxLength?: number;
  rightTopActionsLength?: number;
  leftActionsLoading?: boolean;
  rightActionsLoading?: boolean;
  useShortView?: boolean;
  hideMenu?: boolean;
  id?: string;
}

export interface CoverProps {
  isCover?: boolean;
  closeCover?: () => void;
  coverClosed?: boolean;
}

const PageWrapperRoot = styled('div')(({ theme }) => ({
  '&.page-wrapper-root': {
    background: bookitColors.grayscale.grayBackground,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    padding: 0,
    //minWidth: '360px',
    gap: 8,
    '.sidePanelRoot &': {
      background: bookitColors.base.white,
    },
    '&.covered': {
      filter: 'brightness(30%)',
    },
    '&.cover:not(.covered)': {
      // height: '95vh',
      height: 'calc(100dvh - 50px)',
      //height: '100vh',
      '& [class*="tableButtonsRootShort"]': {
        maxWidth: '320px',
      },
    },
    '&.baseNavigationHeight': {
      height: '97dvh',
    },
    '&.baseRootUsePanelHeight': {
      height: '99.9dvh',
    },
    '&.baseRootUsePanelShortHeight': {
      // height: '97vh',
      height: 'calc(100dvh - 50px)',
      minWidth: 'unset',
    },
    '&.baseRootHeight': {
      height: '91dvh',
      '&.subBarExpanded': {
        height: '85dvh',
      },
    },
    '.content': {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'flex-start',
      width: '100%',
      padding: '8px 0 0',
      '&.contentRoot': {
        justifyContent: 'space-between',
        height: '100%',
        overflow: 'hidden',
      },
      '&.contentMobRoot': {
        justifyContent: 'space-between',
        padding: theme.spacing(1, 0, 0, 0),
        height: '100%',
        //height: '90%',
        //gap: theme.spacing(2),
        flex: '0 1 calc(100% - 50px)',
        overflow: 'hidden',
        '&.hideTitleBar': {
          padding: '0px',
          flex: '0 1 100%',
        },
      },
      '&.hideTitleBar': {
        padding: '0px',
      },
      '.contentContainer': {
        display: 'flex',
        flexDirection: 'column',
        padding: 0,
        width: '100%',
        height: '100%',
        position: 'relative',
        overflowY: 'auto',
        gap: '32px',
        '&::-webkit-scrollbar': {
          width: '6px',
          // '&&': {
          //   background: 'yelow',
          // },
        },
        '&::-webkit-scrollbar-thumb': {
          boxShadow: `inset 0 0 2px ${theme.palette.common.grayBorders}`,
          borderRadius: '6px',
          backgroundColor: theme.palette.common.grayBorders,
        },
        '&.showShortView': {
          gap: '16px',
          marginBottom: '16px',
        },
      },
      //TO DO: find a way to add a box-shadow to bottom toolbar in case when content is scrollable so it match to the Figma.
      '.bottomToolbar': {
        position: 'static',
        display: 'flex',
        flexDirection: 'column',
        width: '100%',
        background: bookitColors.base.white,
        borderRadius: 'unset',
        padding: theme.spacing(2, 3, 2, 3),
        boxShadow: bookitShadows.shadowBorderTop,
        marginBottom: 'env(safe-area-inset-bottom, 50px)',
        flexShrink: 0,
        flexGrow: 0,
        '&.showShortView': {
          gap: '8px',
          boxShadow: bookitShadows.shadowBorderTop,
        },
      },
    },
  },
}));

export const PageWrapper = React.memo(function PageWrapper(
  props: PageWrapperProps,
) {
  const {
    pageName,
    titlePage,
    subTitlePage,
    children,
    leftActions,
    rightActions,
    rightTopActions,
    leftTopActions,
    loading,
    onSubmit,
    onClose,
    closeSidePanel,
    closable,
    useSidePanel,
    confirm,
    pageLink,
    toPageLink,
    cover,
    closeCover,
    disableExpandToggle,
    titleTooltip,
    isCover,
    switchState,
    useSwitchActions: useSwithActions,
    topProcessing,
    maxLeftTopButtonsLength,
    rightTopActionsAsMenu,
    hideTitleBar,
    leftActionsMaxLength,
    rightTopActionsLength,
    leftActionsLoading,
    rightActionsLoading,
    useShortView,
    coverClosed,
    hideMenu,
    id,
  } = props;
  // const { t } = useTranslation();
  const isMobile = DetectIsMobile();
  const dispatch = useDispatch();
  const { actions } = useLayoutSlice();
  const sidePanelExpanded = useSelector(selectExpandedSidePanel);
  const coverExpanded = useSelector(selectIsCoverExpanded);
  const hideNavigation = useSelector(selectHideNavigation);
  const isCoverOpen = useSelector(selectIsCoverOpen);
  const collapsedBar = useSelector(selectIsSubBarCollapsed);
  const showGroups = useSelector(selectShowGlobalServiceGroupFilter);
  const isCovered = !!cover;
  const [isCoverClosed, setIsCoverClosed] = React.useState<boolean | undefined>(
    coverClosed,
  );
  const showShortView = React.useMemo(() => {
    return (
      isMobile ||
      (useSidePanel && !sidePanelExpanded) ||
      (isCover && !coverExpanded) ||
      useShortView
    );
  }, [
    coverExpanded,
    isCover,
    isMobile,
    sidePanelExpanded,
    useShortView,
    useSidePanel,
  ]);

  const closePanel = () => {
    if (isCover) {
      closeCover?.();
      dispatch(actions.setIsCoverOpen(false));
      return;
    }
    if (closeSidePanel) {
      closeSidePanel();
    } else {
      dispatch(actions.resetSidePanel());
    }
    if (onClose) {
      onClose();
    }
  };
  const expandPanel = () => {
    if (isCover) {
      dispatch(actions.setIsCoverExpanded(!coverExpanded));
    } else {
      dispatch(actions.setExpanded(!sidePanelExpanded));
    }
  };
  const onCoveredClick = event => {
    if (
      isCovered &&
      isCoverOpen &&
      !!event.target.id &&
      event.target.id.indexOf('page-wrapper-root') > -1
    ) {
      closeCover?.();
      dispatch(actions.setIsCoverOpen(false));
    }
  };
  const theme = useTheme();
  React.useEffect(() => {
    setIsCoverClosed(coverClosed);
  }, [coverClosed]);
  React.useEffect(() => {
    if (isCoverOpen && isCover && isCoverClosed) {
      closeCover?.();
      dispatch(actions.setIsCoverOpen(false));
      setIsCoverClosed(undefined);
    }
  }, [actions, closeCover, isCoverClosed, dispatch, isCover, isCoverOpen]);
  return (
    <React.Fragment>
      <PageWrapperRoot
        theme={theme}
        id={id !== undefined ? `page-wrapper-root-${id}` : 'page-wrapper-root'}
        key="page-wrapper-root"
        onClick={onCoveredClick}
        className={clsx('page-wrapper-root', {
          covered: isCovered,
          cover: isCover || !!cover,
          baseRootUsePanelHeight:
            (useSidePanel || isMobile) &&
            !(useSidePanel && isMobile) &&
            !isCover &&
            !cover,
          baseRootUsePanelShortHeight: useSidePanel && isMobile,
          baseRootHeight: !useSidePanel && !hideNavigation && !isMobile,
          subBarExpanded:
            !useSidePanel &&
            !hideNavigation &&
            !isMobile &&
            !collapsedBar &&
            showGroups &&
            !isCovered,
          baseNavigationHeight: !useSidePanel && hideNavigation,
        })}
      >
        {!hideTitleBar && (
          <React.Fragment>
            {showShortView ? (
              <ShortHead
                closable={closable}
                useSidePanel={useSidePanel}
                closePanel={closePanel}
                pageName={pageName}
                titlePage={titlePage}
                pageLink={pageLink}
                toPageLink={toPageLink}
                titleTooltip={titleTooltip}
                isMobile={isMobile}
                disableExpandToggle={disableExpandToggle}
                expandPanel={expandPanel}
                subTitlePage={subTitlePage}
                leftTopActions={leftTopActions}
                rightTopActions={rightTopActions}
                onSubmit={onSubmit}
                switchState={switchState}
                useSwithActions={useSwithActions && !loading}
                showProcessing={topProcessing}
                loading={!!loading}
                rightTopActionsAsMenu={rightTopActionsAsMenu}
                isCover={isCover || !!cover}
                useShortView={useShortView}
                hideMenu={hideMenu}
              />
            ) : (
              <Head
                closable={closable}
                useSidePanel={useSidePanel}
                closePanel={closePanel}
                pageName={pageName}
                titlePage={titlePage}
                pageLink={pageLink}
                toPageLink={toPageLink}
                titleTooltip={titleTooltip}
                isMobile={isMobile}
                disableExpandToggle={disableExpandToggle || isCover}
                expandPanel={expandPanel}
                subTitlePage={subTitlePage}
                leftTopActions={leftTopActions}
                rightTopActions={rightTopActions}
                onSubmit={onSubmit}
                switchState={switchState}
                useSwithActions={useSwithActions && !loading}
                showProcessing={topProcessing}
                loading={!!loading}
                isCover={isCover}
                rightTopActionsLength={rightTopActionsLength}
                rightActionsLoading={rightActionsLoading}
              />
            )}
          </React.Fragment>
        )}
        <div
          className={clsx('content', {
            contentRoot: !showShortView,
            contentMobRoot: showShortView,
            hideTitleBar: hideTitleBar,
          })}
        >
          <React.Fragment>
            <div
              className={clsx('contentContainer', {
                showShortView: showShortView,
              })}
            >
              {loading ? (
                <FormSkeleton fullPage={!showShortView} />
              ) : (
                <React.Fragment>
                  {showShortView &&
                    ((leftTopActions && leftTopActions.length > 0) ||
                      leftActionsLoading) && (
                      <TopButtonsPanel
                        leftTopActions={leftTopActions}
                        rightTopActions={rightTopActions}
                        showShortView={showShortView}
                        onSubmit={onSubmit}
                        expandPanel={expandPanel}
                        maxLeftTopButtonsLength={maxLeftTopButtonsLength}
                        leftActionsLoading={leftActionsLoading}
                      />
                    )}
                  <React.Fragment>{children}</React.Fragment>
                </React.Fragment>
              )}
            </div>
            <React.Fragment>
              {confirm}
              {leftActions && leftActions?.length > 0 && (
                <div
                  className={clsx('bottomToolbar', {
                    showShortView: showShortView,
                  })}
                >
                  {/* {showShortView &&
                    !isMobile &&
                    useSidePanel &&
                    !disableExpandToggle &&
                    leftActions &&
                    leftActions?.length > 1 && (
                      <React.Fragment>
                        {loading ? (
                          <div className="showMore">
                            <Skeleton width="8ch" />
                          </div>
                        ) : (
                          <div className="showMore">
                            <Button
                              variant="text"
                              size="small"
                              onClick={expandPanel}
                            >
                              <Body bold={true} size="small">
                                {sidePanelExpanded
                                  ? t(translations.ShowLess)
                                  : t(translations.ShowMore)}
                              </Body>
                            </Button>
                          </div>
                        )}
                      </React.Fragment>
                    )} */}
                  {loading ? (
                    <ButtonsSkeleton />
                  ) : (
                    <ButtonsPanel
                      leftActions={leftActions}
                      rightActions={rightActions}
                      isMobile={isMobile}
                      sidePanelExpanded={sidePanelExpanded}
                      disableExpandToggle={disableExpandToggle || isCover}
                      useSidePanel={useSidePanel}
                      onSubmit={onSubmit}
                      expandPanel={expandPanel}
                      leftActionsMaxLength={leftActionsMaxLength}
                    />
                  )}
                </div>
              )}
            </React.Fragment>
          </React.Fragment>
        </div>
        {useSidePanel && !!cover && (
          <CoverWrapper onClose={closeCover} open={isCovered}>
            {cover}
          </CoverWrapper>
        )}
      </PageWrapperRoot>
    </React.Fragment>
  );
});
